import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, RefreshCw, XCircle } from 'lucide-react'
import { getSignupReadiness, type ReadinessCheck } from '@/api/billingAdmin'

/** Instance Admin tab names, for checks fixed outside Billing. */
const TAB_LABEL: Record<string, string> = {
  ai: 'AI Settings', access: 'Access & Waitlist', billing: 'Billing',
}

/**
 * "Can a new user get from sign-up to a built app, run out of credits, and
 * carry on — on this instance, as configured now?" Every row asks the same
 * function the product path asks (backend services/billing/readiness.py), so
 * green here means a real sign-up works. Collapses to one line once ready.
 */
export default function ReadinessPanel({ onOpenSub }: { onOpenSub: (sub: string) => void }) {
  const [checks, setChecks] = useState<ReadinessCheck[] | null>(null)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getSignupReadiness()
      setChecks(res.checks)
      setReady(res.ready)
      setFailed(false)
      // Open by default only when something blocks a new user.
      setExpanded(!res.ready)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (failed) {
    return (
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
        Could not check sign-up readiness. <button onClick={() => void load()} style={linkStyle}>Try again</button>
      </div>
    )
  }
  if (!checks) return null

  const problems = checks.filter((c) => !c.ok)
  const summary = ready
    ? (problems.length
      ? `New users can sign up and build — ${problems.length} thing${problems.length === 1 ? '' : 's'} could be better.`
      : 'New users can sign up, build on their welcome credits, and buy more.')
    : `${problems.filter((c) => c.level === 'error').length} thing(s) stop new users from getting started.`

  return (
    <div className="card" style={{
      padding: '0.75rem 1rem', marginBottom: '1.25rem',
      border: `1px solid ${ready ? 'var(--color-border)' : 'var(--color-error)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
          style={{ ...linkStyle, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, textAlign: 'left' }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {ready
            ? <CheckCircle2 size={16} style={{ color: 'var(--color-success)' }} />
            : <XCircle size={16} style={{ color: 'var(--color-error)' }} />}
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Sign-up readiness</span>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{summary}</span>
        </button>
        <button onClick={() => void load()} disabled={loading} title="Check again" style={linkStyle}>
          <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
        </button>
      </div>

      {expanded && (
        <ul style={{ listStyle: 'none', margin: '0.75rem 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {checks.map((c) => (
            <li key={c.key} style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start', fontSize: '0.875rem' }}>
              <span style={{ marginTop: 1, display: 'flex' }}>
                {c.ok
                  ? <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} />
                  : c.level === 'error'
                    ? <XCircle size={15} style={{ color: 'var(--color-error)' }} />
                    : <AlertTriangle size={15} style={{ color: 'var(--color-warning)' }} />}
              </span>
              <span style={{ flex: 1 }}>
                <strong>{c.label}</strong>
                <span style={{ display: 'block', color: 'var(--color-text-secondary)', marginTop: 2 }}>{c.detail}</span>
              </span>
              {!c.ok && c.fix && (
                c.fix.tab === 'billing' && c.fix.sub
                  ? <button onClick={() => onOpenSub(c.fix!.sub)} style={linkStyle}>Fix</button>
                  : <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                      {TAB_LABEL[c.fix.tab] || c.fix.tab} tab
                    </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const linkStyle = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: 'var(--color-accent)', fontSize: '0.875rem',
} as const
